import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import { useTheme } from '@material-ui/core/styles';
import {
  ChartRow,
  MetricUnit,
  Series,
  TimeRange,
  formatAxisValue,
  formatTick,
  formatValue,
  seriesColor,
  toChartRows,
} from './lib';

/**
 * Crosshair tooltip. Values wear text tokens, never the series color — the
 * colored dot beside each row carries identity, so the text stays legible
 * against the tooltip surface in both modes.
 */
const MetricTooltip = ({
  active,
  payload,
  label,
  unit,
  range,
}: {
  active?: boolean;
  payload?: Array<{ name?: string; value?: number | null; color?: string }>;
  label?: number;
  unit: MetricUnit;
  range: TimeRange;
}) => {
  const theme = useTheme();
  if (!active || !payload?.length) {
    return null;
  }
  return (
    <Paper elevation={3} style={{ padding: '8px 12px' }}>
      <Typography variant="caption" color="textSecondary">
        {label === undefined ? '' : formatTick(label, range)}
      </Typography>
      {payload.map(entry => (
        <div
          key={entry.name}
          style={{ display: 'flex', alignItems: 'center', gap: 8 }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: entry.color,
              flex: '0 0 auto',
            }}
          />
          <Typography
            variant="body2"
            style={{ color: theme.palette.text.primary }}
          >
            {entry.name}
          </Typography>
          <Typography
            variant="body2"
            style={{
              marginLeft: 'auto',
              fontVariantNumeric: 'tabular-nums',
              color: theme.palette.text.primary,
            }}
          >
            {formatValue(entry.value ?? null, unit)}
          </Typography>
        </div>
      ))}
    </Paper>
  );
};

export const MetricChart = ({
  series,
  unit,
  range,
}: {
  series: Series[];
  unit: MetricUnit;
  range: TimeRange;
}) => {
  const theme = useTheme();
  const mode = theme.palette.type === 'dark' ? 'dark' : 'light';
  const rows: ChartRow[] = toChartRows(series);

  // One y-axis for every series: the metric catalog only groups metrics that
  // share a unit, so a second scale is never needed. Two scales on one chart
  // makes the crossing point meaningless.
  return (
    <ResponsiveContainer width="100%" height={260}>
      <LineChart data={rows} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
        <CartesianGrid
          stroke={theme.palette.divider}
          strokeDasharray="3 3"
          vertical={false}
        />
        <XAxis
          dataKey="t"
          type="number"
          scale="time"
          domain={['dataMin', 'dataMax']}
          tickFormatter={(t: number) => formatTick(t, range)}
          stroke={theme.palette.text.secondary}
          tick={{ fontSize: 12 }}
          minTickGap={40}
        />
        <YAxis
          tickFormatter={(v: number) => formatAxisValue(v, unit)}
          stroke={theme.palette.text.secondary}
          tick={{ fontSize: 12 }}
          width={64}
        />
        <Tooltip
          content={<MetricTooltip unit={unit} range={range} />}
          cursor={{ stroke: theme.palette.text.secondary, strokeWidth: 1 }}
        />
        {/* Always present for two or more series, so identity is never colour alone. */}
        <Legend verticalAlign="bottom" height={28} iconType="plainline" />
        {series.map((s, index) => (
          <Line
            key={s.name}
            type="monotone"
            dataKey={s.name}
            stroke={seriesColor(mode, index)}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
            connectNulls={false}
            isAnimationActive={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};
